/*
 * Tea Sets page interactions.
 * Scoped to [data-tea-sets]; vanilla, idempotent (safe on Theme Editor reloads).
 * State lives in the DOM (data-selected / aria-checked / data-open); this module
 * only toggles it and recomputes prices. Add-to-cart posts all selected variants
 * in one /cart/add.js request.
 */
(function () {
  'use strict';

  /* ---- money ---- */
  function formatMoney(cents, format) {
    var value = '';
    var fmt = format || '{{amount}}';
    cents = typeof cents === 'string' ? cents.replace('.', '') : cents;
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function num(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents = parts[1] ? decimal + parts[1] : '';
      return dollars + cents;
    }

    var match = fmt.match(placeholder);
    if (!match) return fmt;
    switch (match[1]) {
      case 'amount': value = num(cents, 2); break;
      case 'amount_no_decimals': value = num(cents, 0); break;
      case 'amount_with_comma_separator': value = num(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = num(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = num(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = num(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = num(cents, 0, ' ', ''); break;
      default: value = num(cents, 2);
    }
    // display rule: whole shekels only — drop trailing ".00" (see DESIGN_SYSTEM.md)
    return fmt.replace(placeholder, value).replace('.00', '').replace(/<[^>]*>/g, '').trim();
  }

  /* ---- pluralization ---- */
  function pluralForm(count, lang) {
    if (lang && lang.indexOf('ru') === 0) {
      var mod10 = count % 10;
      var mod100 = count % 100;
      if (mod10 === 1 && mod100 !== 11) return 'one';
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few';
      return 'many';
    }
    return count === 1 ? 'one' : 'other';
  }

  function pluralize(count, forms, lang) {
    var form = pluralForm(count, lang);
    var word = forms[form] || forms.other || forms.many || '';
    return count + ' ' + word;
  }

  function cents(el, attr) {
    return parseInt(el.getAttribute(attr || 'data-price'), 10) || 0;
  }

  function setStatus(setEl, type, message) {
    var status = setEl.querySelector('[data-set-status]');
    if (!status) return;
    status.textContent = message || '';
    if (type) status.setAttribute('data-status', type);
    else status.removeAttribute('data-status');
    status.setAttribute('role', type === 'error' ? 'alert' : 'status');
    status.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');
  }

  function recompute(setEl, cfg) {
    setStatus(setEl, '', '');
    var rows = setEl.querySelectorAll('[data-tea-row]');
    var sumPrice = 0;
    var sumCompare = 0;
    var nTeas = 0;
    var availTotal = 0;
    rows.forEach(function (row) {
      if (row.getAttribute('data-available') !== 'true') return;
      availTotal += 1;
      if (row.getAttribute('data-selected') === 'true') {
        sumPrice += cents(row);
        sumCompare += cents(row, 'data-compare');
        nTeas += 1;
      }
    });
    var savings = sumCompare - sumPrice;

    var accSum = 0;
    var accSelected = [];
    setEl.querySelectorAll('[data-acc-card]').forEach(function (acc) {
      if (acc.getAttribute('data-selected') === 'true') {
        accSum += cents(acc);
        accSelected.push(acc.getAttribute('data-name'));
      }
    });

    // gift is only available with the full set: deselecting any tea force-disables it
    var giftEl = setEl.querySelector('[data-tea-gift]');
    var giftOn = false;
    var giftPrice = 0;
    if (giftEl) {
      var sw = giftEl.querySelector('[data-gift-toggle]');
      var fullSet = availTotal > 0 && nTeas === availTotal;
      giftEl.setAttribute('data-gift-disabled', fullSet ? 'false' : 'true');
      if (sw) {
        sw.disabled = !fullSet;
        if (!fullSet) sw.setAttribute('aria-checked', 'false');
        giftOn = sw.getAttribute('aria-checked') === 'true';
      }
      if (giftOn) giftPrice = cents(giftEl);
    }

    var total = sumPrice + accSum + giftPrice;

    // header price / old / savings
    var priceEl = setEl.querySelector('[data-set-price]');
    var oldEl = setEl.querySelector('[data-set-old]');
    var savingsEl = setEl.querySelector('[data-set-savings]');
    if (priceEl) priceEl.textContent = formatMoney(sumPrice, cfg.money);
    if (oldEl) {
      if (savings > 0) { oldEl.hidden = false; oldEl.textContent = formatMoney(sumCompare, cfg.money); }
      else oldEl.hidden = true;
    }
    if (savingsEl) {
      if (savings > 0) { savingsEl.hidden = false; savingsEl.textContent = '−' + formatMoney(savings, cfg.money); }
      else savingsEl.hidden = true;
    }

    // total
    var totalEl = setEl.querySelector('[data-set-total]');
    if (totalEl) totalEl.textContent = formatMoney(total, cfg.money);

    // summary
    var parts = [pluralize(nTeas, cfg.tea, cfg.lang)];
    if (accSelected.length === 1) parts.push(accSelected[0]);
    else if (accSelected.length > 1) parts.push(pluralize(accSelected.length, cfg.acc, cfg.lang));
    if (giftOn) parts.push(cfg.giftWord);
    var summaryEl = setEl.querySelector('[data-set-summary]');
    if (summaryEl) summaryEl.textContent = parts.join(' + ');

    // CTA disabled when nothing selected
    var addBtn = setEl.querySelector('[data-set-add]');
    if (addBtn) addBtn.disabled = nTeas === 0;
  }

  /* ---- accordion ---- */
  function toggleSet(root, setEl, cfg) {
    var open = setEl.getAttribute('data-open') !== 'true';
    if (open && cfg.singleOpen) {
      root.querySelectorAll('[data-tea-set]').forEach(function (other) {
        if (other !== setEl) setOpen(other, false, cfg);
      });
    }
    setOpen(setEl, open, cfg);
    if (open) scrollSetIntoView(setEl);
  }

  // Sum the height of any sticky/fixed site header so the expanded set lands just
  // below it instead of under it.
  function stickyHeaderOffset() {
    var offset = 0;
    document
      .querySelectorAll('sticky-header, .header-wrapper, .shopify-section-header, .section-header')
      .forEach(function (el) {
        var pos = getComputedStyle(el).position;
        if (pos === 'sticky' || pos === 'fixed') offset = Math.max(offset, el.offsetHeight);
      });
    return offset;
  }

  // On expand, scroll the set to the top (single-open collapses siblings above it,
  // which would otherwise make it jump under the fold).
  function scrollSetIntoView(setEl) {
    requestAnimationFrame(function () {
      var top = window.pageYOffset + setEl.getBoundingClientRect().top - stickyHeaderOffset() - 8;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: top < 0 ? 0 : top, behavior: reduce ? 'auto' : 'smooth' });
    });
  }

  function setOpen(setEl, open, cfg) {
    setEl.setAttribute('data-open', open ? 'true' : 'false');
    var header = setEl.querySelector('[data-set-header]');
    if (header) header.setAttribute('aria-expanded', open ? 'true' : 'false');
    var label = setEl.querySelector('[data-set-toggle-label]');
    if (label) label.textContent = open ? cfg.strHide : cfg.strShow;
  }

  /* ---- bottom sheet ---- */
  function openSheet(sheet, btn) {
    var set = function (sel, val, isHtml) {
      var el = sheet.querySelector(sel);
      if (el) el[isHtml ? 'innerHTML' : 'textContent'] = val;
    };
    set('[data-sheet-title]', btn.getAttribute('data-name'));
    var sub = [btn.getAttribute('data-sub'), btn.getAttribute('data-weight'), btn.getAttribute('data-price')]
      .filter(function (x) { return x; }).join(' · ');
    set('[data-sheet-sub]', sub);
    set('[data-sheet-desc]', btn.getAttribute('data-desc'));

    /* Лист один на секцию: гасим прошлый кадр и держим spinner, пока не
       приедет новый — иначе поверх нового чая секунду видно предыдущий. */
    var img = sheet.querySelector('[data-sheet-media]');
    var src = btn.getAttribute('data-image');
    var srcset = btn.getAttribute('data-image-srcset');
    if (img) {
      var mediaBox = img.closest('[data-popup-media]');
      if (window.DSPopupMedia && mediaBox) {
        window.DSPopupMedia.swap(mediaBox, {
          src: src,
          srcset: srcset,
          sizes: '(min-width: 750px) 460px, 100vw'
        });
        if (!src) mediaBox.setAttribute('data-media-loading', 'false');
      } else if (src) {
        if (srcset) {
          img.srcset = srcset;
          img.sizes = '(min-width: 750px) 460px, 100vw';
        } else {
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
        }
        img.src = src;
      } else {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.removeAttribute('src');
      }
      img.classList.toggle('tea-sheet__media--placeholder', !src);
    }

    var specs = sheet.querySelector('[data-sheet-specs]');
    if (specs) {
      var cfg = sheet._cfg || {};
      var rows = [
        [cfg.specTaste, btn.getAttribute('data-taste')],
        [cfg.specBrew, btn.getAttribute('data-brew')],
        [cfg.specSuited, btn.getAttribute('data-suited')]
      ];
      specs.innerHTML = '';
      rows.forEach(function (r) {
        if (!r[1]) return;
        var row = document.createElement('div');
        row.className = 'tea-sheet__spec';
        var l = document.createElement('span'); l.className = 'tea-sheet__spec-label'; l.textContent = r[0];
        var v = document.createElement('span'); v.className = 'tea-sheet__spec-value'; v.textContent = r[1];
        row.appendChild(l); row.appendChild(v); specs.appendChild(row);
      });
    }

    var link = sheet.querySelector('[data-sheet-link]');
    if (link) link.href = btn.getAttribute('data-url') || '#';

    sheet._trigger = btn;
    sheet.setAttribute('data-open', 'true');
    sheet.setAttribute('aria-hidden', 'false');
    sheet._bodyWasLocked = document.body.classList.contains('overflow-hidden');
    document.body.classList.add('overflow-hidden');
    var panel = sheet.querySelector('.tea-sheet__panel');
    if (panel) {
      if (sheet._drag) sheet._drag.reset();
      enableSheetDrag(sheet, panel);
      if (typeof trapFocus === 'function') trapFocus(panel, panel);
      else panel.focus();
    }
  }

  /* Перетаскивание шита пальцем — общий helper темы (ds-sheet.js). */
  function enableSheetDrag(sheet, panel) {
    if (sheet._drag || !window.DSSheet) return;
    sheet._drag = window.DSSheet.dragToDismiss(panel, {
      isEnabled: function () {
        return window.matchMedia('(max-width: 749px)').matches
          && sheet.getAttribute('data-open') === 'true'
          && !sheet._closing;
      },
      onDismiss: function () { closeSheet(sheet); },
    });
  }

  function closeSheet(sheet) {
    if (sheet.getAttribute('data-open') !== 'true' || sheet._closing) return;
    var panel = sheet.querySelector('.tea-sheet__panel');

    if (!panel || !window.DSSheet) {
      finishCloseSheet(sheet);
      return;
    }

    sheet._closing = true;
    window.DSSheet.flyOut(panel, function () { finishCloseSheet(sheet); }, {
      up: !window.matchMedia('(max-width: 749px)').matches,
    });
  }

  function finishCloseSheet(sheet) {
    var trigger = sheet._trigger;
    sheet._closing = false;
    sheet.setAttribute('data-open', 'false');
    sheet.setAttribute('aria-hidden', 'true');
    if (!sheet._bodyWasLocked) document.body.classList.remove('overflow-hidden');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(trigger && document.contains(trigger) ? trigger : null);
    } else if (trigger && document.contains(trigger)) {
      trigger.focus();
    }
    if (sheet._drag) sheet._drag.reset();
    sheet._trigger = null;
    sheet._bodyWasLocked = false;
  }

  /* ---- add to cart ---- */
  function addSet(setEl, btn, cfg) {
    if (btn.disabled || btn.classList.contains('loading')) return;
    // "_"-prefixed property is hidden from the customer but visible to staff on
    // the order / packing slip, so the packer can group the lines back into a set.
    var setTitle = setEl.getAttribute('data-set-title');
    // Конструктор — всегда отдельная группа в корзине (решение 2026-08-24),
    // поэтому ключ уникален на каждое добавление и никогда не мёржится.
    var setKey = (setEl.getAttribute('data-set-handle') || 'set') +
      '|b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    var props = setTitle ? { '_Сет': setTitle, '_СетКлюч': setKey } : null;
    function item(el) {
      var it = { id: Number(el.getAttribute('data-variant-id')), quantity: 1 };
      if (props) it.properties = props;
      return it;
    }
    var items = [];
    setEl.querySelectorAll('[data-tea-row]').forEach(function (row) {
      if (row.getAttribute('data-selected') === 'true' && row.getAttribute('data-available') === 'true') {
        items.push(item(row));
      }
    });
    setEl.querySelectorAll('[data-acc-card]').forEach(function (acc) {
      if (acc.getAttribute('data-selected') === 'true') {
        items.push(item(acc));
      }
    });
    var giftEl = setEl.querySelector('[data-tea-gift]');
    if (giftEl) {
      var sw = giftEl.querySelector('[data-gift-toggle]');
      if (sw && sw.getAttribute('aria-checked') === 'true') {
        items.push(item(giftEl));
      }
    }
    if (!items.length) return;

    setStatus(setEl, '', '');
    btn.classList.add('loading');
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    var routes = window.routes || {};
    var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    var body = { items: items };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender()
        .map(function (s) { return s.section || s.id; })
        .filter(Boolean);
      body.sections_url = window.location.pathname;
    }

    return fetch(routes.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.json().then(function (response) {
          if (!r.ok) throw new Error(response.description || response.message || 'Cart add failed');
          return response;
        });
      })
      .then(function (response) {
        if (response.status && response.status >= 400) throw new Error(response.description || 'Cart add failed');
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(response);
          setStatus(setEl, 'success', cfg.addSuccess);
        } else {
          window.location = routes.cart_url || '/cart';
        }
      })
      .catch(function () {
        setStatus(setEl, 'error', cfg.addError);
      })
      .then(function () {
        btn.classList.remove('loading');
        btn.removeAttribute('aria-busy');
        btn.disabled = !setEl.querySelector('[data-tea-row][data-selected="true"][data-available="true"]');
      });
  }

  /* ---- init ---- */
  function readForms(root, prefix) {
    return {
      one: root.getAttribute('data-' + prefix + '-one'),
      few: root.getAttribute('data-' + prefix + '-few'),
      many: root.getAttribute('data-' + prefix + '-many'),
      other: root.getAttribute('data-' + prefix + '-other')
    };
  }

  function init(root) {
    if (root._teaSetsInit) return;
    root._teaSetsInit = true;

    var cfg = {
      money: root.getAttribute('data-money-format'),
      singleOpen: root.getAttribute('data-single-open') === 'true',
      lang: (document.documentElement.lang || 'en').toLowerCase(),
      tea: readForms(root, 'tea'),
      acc: readForms(root, 'acc'),
      giftWord: root.getAttribute('data-summary-gift'),
      strShow: root.getAttribute('data-str-show'),
      strHide: root.getAttribute('data-str-hide'),
      addSuccess: root.getAttribute('data-add-success'),
      addError: root.getAttribute('data-add-error')
    };
    var sheet = root.querySelector('[data-tea-sheet]');
    if (sheet) {
      sheet._cfg = {
        specTaste: root.getAttribute('data-spec-taste'),
        specBrew: root.getAttribute('data-spec-brew'),
        specSuited: root.getAttribute('data-spec-suited')
      };
    }

    root.addEventListener('click', function (e) {
      var header = e.target.closest('[data-set-header]');
      if (header && root.contains(header)) {
        toggleSet(root, header.closest('[data-tea-set]'), cfg);
        return;
      }
      var teaBtn = e.target.closest('[data-tea-toggle]');
      if (teaBtn && root.contains(teaBtn)) {
        var row = teaBtn.closest('[data-tea-row]');
        var sel = row.getAttribute('data-selected') !== 'true';
        row.setAttribute('data-selected', sel ? 'true' : 'false');
        teaBtn.setAttribute('aria-pressed', sel ? 'true' : 'false');
        recompute(row.closest('[data-tea-set]'), cfg);
        return;
      }
      var accBtn = e.target.closest('[data-acc-toggle]');
      if (accBtn && root.contains(accBtn)) {
        var accCard = accBtn.closest('[data-acc-card]');
        var sel2 = accCard.getAttribute('data-selected') !== 'true';
        accCard.setAttribute('data-selected', sel2 ? 'true' : 'false');
        accBtn.setAttribute('aria-pressed', sel2 ? 'true' : 'false');
        recompute(accCard.closest('[data-tea-set]'), cfg);
        return;
      }
      var giftBtn = e.target.closest('[data-gift-toggle]');
      if (giftBtn && root.contains(giftBtn)) {
        var on = giftBtn.getAttribute('aria-checked') !== 'true';
        giftBtn.setAttribute('aria-checked', on ? 'true' : 'false');
        recompute(giftBtn.closest('[data-tea-set]'), cfg);
        return;
      }
      var details = e.target.closest('[data-tea-details]');
      if (details && sheet && root.contains(details)) {
        e.preventDefault();
        openSheet(sheet, details);
        return;
      }
      if (sheet && e.target.closest('[data-sheet-close]')) {
        closeSheet(sheet);
        return;
      }
      var add = e.target.closest('[data-set-add]');
      if (add && root.contains(add)) {
        addSet(add.closest('[data-tea-set]'), add, cfg);
        return;
      }
    });

    // Escape — на уровне документа: привязка к корню секции переставала
    // работать, стоило фокусу уйти за её пределы (например клик по бэкдропу).
    if (window.DSSheet) {
      window.DSSheet.onEscape(
        function () { return sheet && sheet.getAttribute('data-open') === 'true'; },
        function () { closeSheet(sheet); }
      );
    } else {
      root.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && sheet && sheet.getAttribute('data-open') === 'true') {
          closeSheet(sheet);
        }
      });
    }

    // initial compute for every set
    root.querySelectorAll('[data-tea-set]').forEach(function (setEl) { recompute(setEl, cfg); });
  }

  function initAll() {
    document.querySelectorAll('[data-tea-sets]').forEach(init);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);

  // Theme Editor: re-init when a section is loaded.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector) {
      e.target.querySelectorAll('[data-tea-sets]').forEach(init);
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (!e.target || !e.target.querySelectorAll) return;
    e.target.querySelectorAll('[data-tea-sheet][data-open="true"]').forEach(closeSheet);
  });
})();
